import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

// Badges are **non-interactive labels** — no hover, no shadow, no pointer (see
// DESIGN.md › Components › Badges). `secondary` (neutral gray) is the default and
// the standard for status labels so they never read as buttons. Colored variants
// stay available for genuinely semantic state, but never gain hover.
const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold",
  {
    variants: {
      variant: {
        default: "border-transparent bg-secondary text-secondary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive: "border-transparent bg-destructive text-destructive-foreground",
        // Semantic green for genuinely positive state (suitable, completed, signed).
        // Carries its own contrast foreground so callers never hand-roll `bg-success`
        // (which leaves the default dark text → black-on-green).
        success: "border-transparent bg-success text-success-foreground",
        // Amber/blue siblings of `success`/`destructive` — same token-pair rule.
        // `warning` = needs attention (PEP hit, pending review); `info` = neutral
        // informational category. Like all badges: no hover, never interactive.
        warning: "border-transparent bg-warning text-warning-foreground",
        info: "border-transparent bg-info text-info-foreground",
        // `outline` is aliased to the same neutral gray — a bordered/transparent badge
        // reads as an outline button. Status/category labels stay uniformly gray.
        outline: "border-transparent bg-secondary text-secondary-foreground",
      },
    },
    defaultVariants: {
      variant: "secondary",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge };
